import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { createReportAppointmentOrders, getAppointmentOrders, getReportById, getChecksByVisitId, getVisitById, getConversationMessagesByVisitId, getPendingChecksByVisitId, submitCheckResult } from '../api/reportApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import ReportConversationHistory from '../components/ReportConversationHistory';
import HealthHouseCheckList from '../components/HealthHouseCheckList';
import ReportExaminationOrders from '../components/ReportExaminationOrders';
import ReportPrescriptions from '../components/ReportPrescriptions';
import {
  APPOINTMENT_ORDER_OPTION_GROUPS,
  getAppointmentOrderGroupLabel,
  getAppointmentOrderTypeLabel
} from '../utils/appointmentOrderOptions';
import {
  formatDateTime,
  formatReportDateTime,
  getReportLocaleDirection,
  normalizeUiLocale,
  resolveReportLocale,
} from '../utils/locale';

// 添加Markdown样式CSS
const markdownStyles = `
  .markdown-content {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    text-align: start;
  }
  
  .markdown-content h1 {
    color: #2c3e50;
    font-size: 1.8em;
    margin-top: 1.5em;
    margin-bottom: 0.8em;
    padding-bottom: 0.3em;
    border-bottom: 1px solid #eaecef;
    text-align: start;
    font-weight: 700;
  }
  
  .markdown-content h2 {
    color: #3498db;
    font-size: 1.5em;
    margin-top: 1.2em;
    margin-bottom: 0.6em;
    text-align: start;
    font-weight: 600;
  }
  
  .markdown-content h3 {
    color: #2980b9;
    font-size: 1.3em;
    margin-top: 1em;
    margin-bottom: 0.5em;
    text-align: start;
  }
  
  .markdown-content p {
    margin-top: 0.5em;
    margin-bottom: 1em;
    text-align: start;
  }
  
  .markdown-content ul, .markdown-content ol {
    margin-top: 0.5em;
    margin-bottom: 1em;
    padding-inline-start: 2em;
    text-align: start;
  }
  
  .markdown-content li {
    margin-bottom: 0.3em;
    text-align: start;
  }
  
  .markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin-top: 1em;
    margin-bottom: 1em;
  }
  
  .markdown-content th, .markdown-content td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: start;
  }
  
  .markdown-content th {
    background-color: #f2f2f2;
    font-weight: bold;
  }
  
  .markdown-content code {
    background-color: #f4f4f4;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: monospace;
  }
  
  .markdown-content pre {
    background-color: #f4f4f4;
    padding: 10px;
    border-radius: 5px;
    overflow-x: auto;
  }
  
  .markdown-content blockquote {
    border-inline-start: 4px solid #ddd;
    padding-inline-start: 16px;
    margin: 1em 0;
    color: #666;
  }
`;

const SHOW_LEGACY_CHECK_SECTIONS = false;

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const uiLocale = normalizeUiLocale(i18n.resolvedLanguage || i18n.language);
  const appointmentOrdersSectionRef = useRef(null);
  const focusTarget = useMemo(() => new URLSearchParams(location.search).get('focus'), [location.search]);
  
  const [report, setReport] = useState(null);
  const [visit, setVisit] = useState(null);
  const reportLocale = resolveReportLocale(report, visit);
  const reportDirection = getReportLocaleDirection(reportLocale);
  const [reportContent, setReportContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // 检查项相关状态
  const [checks, setChecks] = useState([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksError, setChecksError] = useState('');
  const [, setExpandedChecks] = useState({});

  // 对话消息相关状态
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState('');
  const [showConversations, setShowConversations] = useState(false);

  // 待检查项目相关状态
  const [pendingChecks, setPendingChecks] = useState([]);
  const [pendingChecksLoading, setPendingChecksLoading] = useState(false);
  const [pendingChecksError, setPendingChecksError] = useState('');
  const [appointmentOrders, setAppointmentOrders] = useState([]);
  const [appointmentDrafts, setAppointmentDrafts] = useState([{ check_type: '', description: '' }]);
  const [creatingAppointmentOrders, setCreatingAppointmentOrders] = useState(false);

  // 检查结果输入相关状态
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedPendingCheck, setSelectedPendingCheck] = useState(null);
  const [resultInputType, setResultInputType] = useState('text'); // 'text' or 'select'
  const [resultText, setResultText] = useState('');
  const [resultValue, setResultValue] = useState('');
  const [resultDescription, setResultDescription] = useState('');
  const [submittingResult, setSubmittingResult] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // 按钮样式函数
  const getButtonStyle = (type = 'primary') => {
    const baseStyle = {
      padding: '10px 16px',
      border: '1px solid transparent',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      color: 'var(--text-primary)',
      transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease, opacity 120ms ease',
      textShadow: 'none',
      position: 'relative',
      overflow: 'hidden'
    };

    const styles = {
      primary: {
        ...baseStyle,
        color: '#06231c',
        background: 'var(--accent-cyan)',
        border: '1px solid var(--accent-cyan)',
        boxShadow: 'none'
      },
      success: {
        ...baseStyle,
        color: '#06231c',
        background: 'var(--accent-success)',
        border: '1px solid var(--accent-success)',
        boxShadow: 'none'
      },
      secondary: {
        ...baseStyle,
        background: 'rgba(67, 214, 197, 0.08)',
        border: '1px solid rgba(67, 214, 197, 0.22)',
        boxShadow: 'none'
      },
      dark: {
        ...baseStyle,
        background: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'none'
      }
    };

    return styles[type] || styles.primary;
  };

  const handleButtonHover = (e, isEnter) => {
    if (isEnter) {
      e.target.style.transform = 'none';
    } else {
      e.target.style.transform = 'none';
    }
  };

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await getReportById(id);
        setReport(data);
        
        // 判断content是否存在
        if (typeof data.content === 'string' && data.content.trim() !== '') {
          setReportContent(data.content);
        } else if (data.content && typeof data.content === 'object' && Object.keys(data.content).length > 0) {
          setReportContent(JSON.stringify(data.content, null, 2));
        } else {
          setReportContent('');
        }
        
        // 获取相关的检查项、待检查项和对话消息
        if (data.visit_id) {
          fetchChecks(data.visit_id);
          fetchPendingChecks(data.visit_id);
          fetchMessages(data.visit_id);
          fetchAppointmentOrders(data.visit_id);
          
          try {
            const visitData = await getVisitById(data.visit_id);
            setVisit(visitData);
          } catch (visitError) {
            console.error(`获取就诊信息失败:`, visitError);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('获取报告详情失败:', error.response?.data || error.message || error);
        setError(t('reports.fetchReportError'));
        setLoading(false);
      }
    };
    
    fetchReport();
    // Keep this loader route-driven; helper deps update view state and should not
    // re-run the whole archive fetch after checks/messages change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  // 获取检查项
  const fetchChecks = async (visitId) => {
    if (!visitId) {
      setChecksError(t('reports.noVisitId'));
      setChecksLoading(false);
      return;
    }
    
    try {
      setChecksLoading(true);
      const data = await getChecksByVisitId(visitId);
      setChecks(data || []);
      setChecksError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的检查项失败:`, error.response?.data || error.message || error);
      setChecksError(t('reports.fetchChecksError'));
    } finally {
      setChecksLoading(false);
    }
  };

  // 获取待检查项目列表
  const fetchPendingChecks = async (visitId) => {
    if (!visitId) {
      setPendingChecksError(t('reports.noVisitId'));
      setPendingChecksLoading(false);
      return;
    }

    try {
      setPendingChecksLoading(true);
      const data = await getPendingChecksByVisitId(visitId);
      setPendingChecks(data || []);
      setPendingChecksError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的待检查项失败:`, error.response?.data || error.message || error);
      setPendingChecksError(t('reports.fetchPendingChecksError'));
    } finally {
      setPendingChecksLoading(false);
    }
  };

  const fetchAppointmentOrders = async (visitId) => {
    if (!visitId) {
      setAppointmentOrders([]);
      return;
    }

    try {
      const [sourceOrders, claimOrders] = await Promise.all([
        getAppointmentOrders({ source_visit_id: visitId }),
        getAppointmentOrders({ claim_visit_id: visitId })
      ]);

      const merged = [...(sourceOrders || []), ...(claimOrders || [])];
      const uniqueOrders = merged.filter((order, index, array) => array.findIndex((candidate) => candidate.id === order.id) === index);
      setAppointmentOrders(uniqueOrders);
    } catch (requestError) {
      console.error(`获取就诊ID${visitId}关联预约单失败:`, requestError);
      setAppointmentOrders([]);
    }
  };

  const addAppointmentDraft = () => {
    setAppointmentDrafts((current) => [...current, { check_type: '', description: '' }]);
  };

  const updateAppointmentDraft = (index, field, value) => {
    setAppointmentDrafts((current) =>
      current.map((draft, draftIndex) => (
        draftIndex === index ? { ...draft, [field]: value } : draft
      ))
    );
  };

  const removeAppointmentDraft = (index) => {
    setAppointmentDrafts((current) => {
      if (current.length === 1) {
        return [{ check_type: '', description: '' }];
      }
      return current.filter((_, draftIndex) => draftIndex !== index);
    });
  };

  const handleCreateAppointmentOrders = async () => {
    if (!report?.id) {
      setError(t('reports.fetchReportError'));
      return;
    }

    const appointmentOrdersPayload = appointmentDrafts
      .map((draft) => ({
        check_type: draft.check_type.trim(),
        description: draft.description.trim()
      }))
      .filter((draft) => draft.check_type);

    if (appointmentOrdersPayload.length === 0) {
      setError(t('reports.appointmentOrdersValidationError'));
      return;
    }

    try {
      setCreatingAppointmentOrders(true);
      setError('');
      setSuccessMessage('');

      await createReportAppointmentOrders(report.id, appointmentOrdersPayload);
      await fetchAppointmentOrders(report.visit_id);
      setAppointmentDrafts([{ check_type: '', description: '' }]);
      setSuccessMessage(t('reports.appointmentOrdersCreateSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (requestError) {
      console.error(`为报告ID${report.id}追加预约单失败:`, requestError.response?.data || requestError.message || requestError);
      setError(t('reports.appointmentOrdersCreateError'));
    } finally {
      setCreatingAppointmentOrders(false);
    }
  };

  // 打开检查结果输入模态框
  const openResultModal = (pendingCheck) => {
    setSelectedPendingCheck(pendingCheck);
    setResultText('');
    setResultValue('');
    setResultDescription('');
    setSubmitError('');
    
    // 根据检查类型设置默认输入类型
    const checkType = pendingCheck.check_type?.toLowerCase();
    if (checkType && (checkType.includes('lab') || checkType.includes('blood') || checkType.includes('quick'))) {
      setResultInputType('select');
      setResultValue('阴性'); // 默认值
    } else {
      setResultInputType('text');
    }
    
    setShowResultModal(true);
  };

  // 关闭检查结果输入模态框
  const closeResultModal = () => {
    setShowResultModal(false);
    setSelectedPendingCheck(null);
    setResultText('');
    setResultValue('');
    setResultDescription('');
    setSubmitError('');
  };

  // 提交检查结果
  const submitResult = async () => {
    if (!selectedPendingCheck) return;
    
    // 验证输入
    if (resultInputType === 'text' && !resultText.trim()) {
      setSubmitError(t('reports.pleaseEnterResult'));
      return;
    }
    
    if (resultInputType === 'select' && !resultValue) {
      setSubmitError(t('reports.pleaseSelectResult'));
      return;
    }

    setSubmittingResult(true);
    setSubmitError('');

    try {
      // 构建CheckItem数组，参考ReportSubmissionView.swift的结构
      const checkItems = [];

      // 添加检查结果（移除不需要的 report_type）
      const finalResult = resultInputType === 'text' ? resultText.trim() : resultValue;
      checkItems.push({
        name: "check_result",
        name_local: selectedPendingCheck.description || t('reports.checkResult'),
        value: finalResult,
        unit: null,
        reference_range: null,
        description: resultDescription.trim() || null,
        description_local: resultDescription.trim() || null
      });

      // 调用API提交结果
      await submitCheckResult({
        type: selectedPendingCheck.check_type || 'other',
        blob: null,
        metadata: checkItems,
        blobType: "application/json",
        pendingCheckId: selectedPendingCheck.id,
        visitId: report.visit_id
      });

      // 提交成功后刷新待检查项目列表和检查项列表
      if (report?.visit_id) {
        await fetchPendingChecks(report.visit_id);
        await fetchChecks(report.visit_id);
      }
      
      closeResultModal();
      
      // 移除 alert，不显示成功消息
      
    } catch (error) {
      console.error('提交检查结果失败:', error.response?.data || error.message || error);
      setSubmitError(t('reports.submitResultError'));
    } finally {
      setSubmittingResult(false);
    }
  };

  // 导出 Markdown 文件
  const handleExportMarkdown = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 创建 Blob 对象
      const blob = new Blob([reportContent], { type: 'text/markdown;charset=utf-8' });
      
      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // 生成文件名：report_[报告ID]_[时间戳].md
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `report_${id}_${timestamp}.md`;
      
      // 触发下载
      document.body.appendChild(link);
      link.click();
      
      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出Markdown文件失败:', error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  // 导出 PDF 文件（使用浏览器打印功能）
  const handleExportPDF = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 创建一个新窗口用于打印
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError(t('reports.exportError'));
        setTimeout(() => setError(''), 3000);
        return;
      }

      // 渲染Markdown为HTML
      const markdownContent = document.querySelector('.markdown-content');
      const htmlContent = markdownContent ? markdownContent.innerHTML : reportContent;

      // 生成打印页面的HTML
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="${reportLocale}" dir="${reportDirection}">
        <head>
          <meta charset="utf-8">
          <title>${t('reports.reportContent')} #${id} - ${timestamp}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Microsoft YaHei", "微软雅黑";
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 20px auto;
              padding: 20px;
              text-align: start;
            }
            h1 {
              color: #2c3e50;
              font-size: 1.8em;
              margin-top: 1.5em;
              margin-bottom: 0.8em;
              padding-bottom: 0.3em;
              border-bottom: 1px solid #eaecef;
            }
            h2 {
              color: #3498db;
              font-size: 1.5em;
              margin-top: 1.2em;
              margin-bottom: 0.6em;
            }
            h3 {
              color: #2980b9;
              font-size: 1.3em;
              margin-top: 1em;
              margin-bottom: 0.5em;
            }
            p {
              margin-top: 0.5em;
              margin-bottom: 1em;
            }
            ul, ol {
              margin-top: 0.5em;
              margin-bottom: 1em;
              padding-inline-start: 2em;
            }
            li {
              margin-bottom: 0.3em;
            }
            table {
              border-collapse: collapse;
              width: 100%;
              margin: 1em 0;
            }
            table th {
              background-color: #f8f9fa;
              border: 1px solid #dfe2e5;
              padding: 0.5em 1em;
              font-weight: 600;
            }
            table td {
              border: 1px solid #dfe2e5;
              padding: 0.5em 1em;
            }
            @media print {
              body {
                margin: 0;
                padding: 15mm;
              }
            }
          </style>
        </head>
        <body>
          <h1>${t('reports.reportContent')} #${id}</h1>
          <p style="color: #666; font-size: 0.9em;">${t('reports.createdAt')}: ${formatReportDateTime(new Date(), reportLocale)}</p>
          <hr style="margin: 20px 0; border: none; border-top: 2px solid #eee;">
          ${htmlContent}
        </body>
        </html>
      `);
      printWindow.document.close();

      // 等待内容加载完成后触发打印
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };

      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出PDF失败:', error.message || error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  // 导出 DOCX 文件
  const handleExportDOCX = () => {
    if (!reportContent || reportContent.trim() === '') {
      setError(t('reports.noContentToExport'));
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      // 将Markdown转换为简单的HTML格式
      let htmlContent = reportContent
        // 标题转换
        .replace(/^### (.*$)/gim, '<h3>$1</h3>')
        .replace(/^## (.*$)/gim, '<h2>$1</h2>')
        .replace(/^# (.*$)/gim, '<h1>$1</h1>')
        // 粗体和斜体
        .replace(/\*\*\*(.*?)\*\*\*/g, '<b><i>$1</i></b>')
        .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
        .replace(/\*(.*?)\*/g, '<i>$1</i>')
        // 列表
        .replace(/^\* (.*$)/gim, '<li>$1</li>')
        .replace(/^- (.*$)/gim, '<li>$1</li>')
        .replace(/^\d+\. (.*$)/gim, '<li>$1</li>')
        // 段落
        .replace(/\n\n/g, '</p><p>')
        // 换行
        .replace(/\n/g, '<br/>');

      // 包装在段落标签中
      htmlContent = '<p>' + htmlContent + '</p>';
      
      // 包装列表项
      htmlContent = htmlContent.replace(/(<li>.*?<\/li>)/gs, (match) => {
        return '<ul>' + match + '</ul>';
      });

      // 创建完整的HTML文档
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fullHTML = `
        <!DOCTYPE html>
        <html lang='${reportLocale}' dir='${reportDirection}' xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>${t('reports.reportContent')} #${id}</title>
          <style>
            body { font-family: Arial, 'Microsoft YaHei', sans-serif; line-height: 1.6; text-align: start; }
            h1 { color: #2c3e50; font-size: 24pt; margin-top: 12pt; margin-bottom: 6pt; }
            h2 { color: #3498db; font-size: 18pt; margin-top: 10pt; margin-bottom: 5pt; }
            h3 { color: #2980b9; font-size: 14pt; margin-top: 8pt; margin-bottom: 4pt; }
            p { margin-top: 6pt; margin-bottom: 6pt; }
            ul, ol { margin-top: 6pt; margin-bottom: 6pt; }
            li { margin-bottom: 3pt; }
          </style>
        </head>
        <body>
          <h1>${t('reports.reportContent')} #${id}</h1>
          <p style="color: #666; font-size: 10pt;">${t('reports.createdAt')}: ${formatReportDateTime(new Date(), reportLocale)}</p>
          <hr/>
          ${htmlContent}
        </body>
        </html>
      `;

      // 创建Blob对象
      const blob = new Blob(['\ufeff', fullHTML], {
        type: 'application/msword'
      });

      // 创建下载链接
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${id}_${timestamp}.doc`;

      // 触发下载
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccessMessage(t('reports.exportSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('导出DOCX失败:', error.message || error);
      setError(t('reports.exportError'));
      setTimeout(() => setError(''), 3000);
    }
  };

  // 获取对话消息
  const fetchMessages = async (visitId) => {
    if (!visitId) {
      setMessagesError(t('reports.noVisitId'));
      setMessagesLoading(false);
      return;
    }

    try {
      setMessagesLoading(true);
      const data = await getConversationMessagesByVisitId(visitId);
      
      // 过滤掉以"人类医生已确认如下报告"开头的消息
      const filteredMessages = (data.messages || []).filter(message => {
        return !message.content || !message.content.startsWith('人类医生已确认如下报告');
      });
      
      setMessages(filteredMessages);
      setMessagesError('');
    } catch (error) {
      console.error(`获取就诊ID${visitId}的对话消息失败:`, error.response?.data || error.message || error);
      setMessagesError(t('reports.fetchConversationError'));
    } finally {
      setMessagesLoading(false);
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return t('reports.notSpecified');
    return formatDateTime(dateString, uiLocale) || t('reports.notSpecified');
  };

  // 格式化报告类型显示
  const formatReportType = (reportType) => {
    if (!reportType) return '';
    
    const typeMap = {
      'proposal': t('reports.proposalReport'),
      'final': t('reports.finalReport')
    };
    
    return typeMap[reportType] || reportType;
  };

  const formatVisitType = (visitType) => {
    if (visitType === 'follow_up') {
      return t('reports.visitTypeFollowUp');
    }
    if (visitType === 'initial') {
      return t('reports.visitTypeInitial');
    }
    if (visitType === 'checkup') {
      return t('reports.visitTypeCheckup');
    }
    return t('reports.visitTypeUnknown');
  };

  const formatRelatedVisits = (relatedVisitIds) => {
    if (!relatedVisitIds || relatedVisitIds.length === 0) {
      return t('reports.noLinkedSourceVisits');
    }

    return relatedVisitIds.map((visitId) => `#${visitId}`).join(' / ');
  };

  const formatClinicLabel = (name, clinicId) => {
    if (name) {
      return name;
    }
    if (clinicId) {
      return `Clinic #${clinicId}`;
    }
    return t('reports.unknownClinic');
  };

  const buildRelatedVisitLink = (visitId) => `/reviewed-reports?visit_id=${visitId}`;
  const buildAppointmentQueueLink = (orderId) => `/appointment-orders?order_id=${orderId}`;
  const getAppointmentOrderDisplay = (order) => (
    order?.description || getAppointmentOrderTypeLabel(order?.check_type, t)
  );
  const examinationOrders = Array.isArray(report?.examination_orders)
    ? report.examination_orders
    : [];
  const prescriptionItems = Array.isArray(report?.prescription_items)
    ? report.prescription_items
    : [];
  const showExaminationOrdersSection = report?.report_type === 'final' && report?.status === 'approved';
  const showPrescriptionSection = report?.report_type === 'final' && report?.status === 'approved';
  const canCreateAppointmentOrders = report?.report_type === 'final' && report?.status === 'approved' && (visit?.status === 'complete' || visit?.status === 'completed');
  const showAppointmentOrdersSection = canCreateAppointmentOrders || appointmentOrders.length > 0;

  useEffect(() => {
    if (focusTarget !== 'appointment-orders' || !showAppointmentOrdersSection || !appointmentOrdersSectionRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      appointmentOrdersSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [focusTarget, showAppointmentOrdersSection]);

  // 获取检查类型显示名称
  const getCheckTypeDisplay = (type) => {
    if (!type) return t('common.unknown');
    
    // 处理 CHECKTYPES.XXX 格式的类型
    let normalizedType = type;
    if (type.startsWith('CHECKTYPES.')) {
      const typeKey = type.replace('CHECKTYPES.', '').toLowerCase();
      // 映射常见的检查类型
      const typeMapping = {
        'lab': 'blood',
        'quick': 'other',
        'pure_throat': 'throat',
        'physical': 'common',
        'vital_signs': 'vital_signs',
        'urine': 'urine',
        'blood': 'blood',
        'ct': 'ct',
        'xray': 'xray',
        'ultrasound': 'ultrasound',
        'ecg': 'ecg',
        'mri': 'mri',
        'pathology': 'pathology',
        'endoscopy': 'endoscopy'
      };
      normalizedType = typeMapping[typeKey] || typeKey;
    }
    
    return t(`checkTypes.${normalizedType}`) || type;
  };

  // 切换检查项详情显示
  const toggleCheckDetails = (index) => {
    const detailsElement = document.getElementById(`check-details-${index}`);
    if (detailsElement) {
      if (detailsElement.style.display === 'none' || !detailsElement.style.display) {
        detailsElement.style.display = 'block';
        setExpandedChecks(prev => ({ ...prev, [index]: true }));
      } else {
        detailsElement.style.display = 'none';
        setExpandedChecks(prev => ({ ...prev, [index]: false }));
      }
    }
  };

  // 渲染检查项元数据
  const renderCheckMetadata = (check) => {
    // 检查是否有图片文件
    const hasImage = check.file_url && check.file_mime_type && 
                    (check.file_mime_type.startsWith('image/') || 
                     ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(check.file_mime_type.toLowerCase()));
    
    // 检查是否有音频文件
    const hasAudio = check.file_url && check.file_mime_type && 
                    (check.file_mime_type.startsWith('audio/') || 
                     ['audio/aac', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/mpeg'].includes(check.file_mime_type.toLowerCase()));
    
    // 处理metadata
    let metadataItems = [];
    if (check.metadata && Array.isArray(check.metadata)) {
      metadataItems = check.metadata;
    } else if (check.metadata && typeof check.metadata === 'object') {
      metadataItems = Object.entries(check.metadata).map(([key, value]) => ({
        name: key,
        value: value
      }));
    }
    
    const hasMetadata = metadataItems.length > 0;
    
    // 如果既没有图片、音频也没有metadata，显示无数据
    if (!hasImage && !hasAudio && !hasMetadata) {
      return <p style={{ color: '#334255' }}>{t('reports.noMetadata')}</p>;
    }
    
    return (
      <div className="check-metadata">
        {/* 显示图片 */}
        {hasImage && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{t('reports.checkImage')}</h4>
            <div style={{
              border: '2px solid #dde6e9',
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              textAlign: 'center'
            }}>
              <img 
                src={check.file_url} 
                alt={`${getCheckTypeDisplay(check.check_type)} 检查图片`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none', color: '#556476', marginTop: '10px' }}>
                {t('reports.imageLoadError')}
              </div>
              <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#556476' }}>
                {t('reports.fileType')}: {check.file_mime_type}
              </div>
            </div>
          </div>
        )}
        
        {/* 显示音频 */}
        {hasAudio && (
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ marginBottom: '10px', color: '#2c3e50' }}>{t('reports.checkAudio')}</h4>
            <div style={{
              border: '2px solid #dde6e9',
              borderRadius: '8px',
              padding: '15px',
              backgroundColor: '#f8f9fa'
            }}>
              <audio 
                controls 
                style={{
                  width: '100%',
                  marginBottom: '10px'
                }}
                preload="metadata"
              >
                <source src={check.file_url} type={check.file_mime_type} />
                {t('reports.audioNotSupported')}
              </audio>
              <div style={{ fontSize: '0.9em', color: '#556476' }}>
                {t('reports.fileType')}: {check.file_mime_type}
              </div>
              <div style={{ fontSize: '0.9em', color: '#556476', marginTop: '5px' }}>
                <a 
                  href={check.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    color: '#1d6a86',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                  {t('reports.downloadAudio')}
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* 显示metadata */}
        {hasMetadata && (
          <div>
            <h4 style={{ color: '#14253a' }}>{t('reports.checkItemData')}</h4>
            <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ 
                    textAlign: 'start',
                    padding: '8px', 
                    backgroundColor: '#eef7fa', 
                    border: '1px solid #dde6e9',
                    fontWeight: '600'
                  }}>
                    {t('reports.name')}
                  </th>
                  <th style={{ 
                    textAlign: 'start',
                    padding: '8px', 
                    backgroundColor: '#eef7fa', 
                    border: '1px solid #dde6e9',
                    fontWeight: '600'
                  }}>
                    {t('reports.value')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {metadataItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ 
                      padding: '8px', 
                      border: '1px solid #dde6e9'
                    }}>
                      {item.name_local || item.name}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      border: '1px solid #dde6e9'
                    }}>
                      {item.value !== undefined && item.value !== null
                        ? <span>
                            {String(item.value)}
                            {item.unit ? ` ${item.unit}` : ''}
                            {item.reference_range ? ` (${t('reports.referenceRange')}: ${item.reference_range})` : ''}
                          </span>
                        : t('reports.noData')
                      }
                      {item.description_local || item.description
                        ? <div style={{ marginTop: '5px', color: '#556476', fontSize: '0.9em' }}>
                            {item.description_local || item.description}
                          </div>
                        : null
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <style>{markdownStyles}</style>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid #f3f3f3',
          borderRadius: '50%',
          borderTopColor: '#667eea',
          animation: 'spin 1s linear infinite',
          marginBottom: '20px'
        }}></div>
        <p style={{ fontSize: '1.2rem', color: '#666' }}>{t('common.loading')}</p>
        
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="report-detail-page report-detail-page--view" style={{
      minHeight: '100vh',
      background: 'transparent',
      padding: '0px',
      position: 'relative'
    }}>
      <style dangerouslySetInnerHTML={{ __html: `
        ${markdownStyles}
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      ` }} />
      
      {/* Back按钮区域 - 左上角 */}
      <div className="report-detail-page__back" style={{
        position: 'absolute', 
        top: '0px', 
        insetInlineStart: '0px',
        zIndex: 1000 
      }}>
        <button 
          onClick={() => navigate('/reviewed-reports')}
          style={getButtonStyle('dark')}
          onMouseEnter={(e) => handleButtonHover(e, true)}
          onMouseLeave={(e) => handleButtonHover(e, false)}
        >
          {t('common.back')}
        </button>
      </div>
      
      <div className="report-detail-page__inner" style={{
        maxWidth: '1200px', 
        margin: '0 auto',
        padding: '0 20px',
        paddingTop: '60px'
      }}>

        {/* 页面标题 */}
        <div className="report-detail-page__header" style={{
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          marginBottom: '30px',
          background: 'rgba(250, 245, 236, 0.94)',
          padding: '20px',
          borderRadius: '16px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div className="report-detail-page__title-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1 style={{
              margin: 0,
              fontSize: '2rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #43d6c5 0%, #7ae5ba 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {t('reports.viewReport')} #{id}
            </h1>
            {report && report.report_type && (
              <span style={{
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                background: report.report_type === 'proposal' 
                  ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(39, 174, 96, 0.1) 100%)',
                color: report.report_type === 'proposal' ? '#667eea' : '#27ae60',
                border: report.report_type === 'proposal' 
                  ? '1px solid rgba(102, 126, 234, 0.3)' 
                  : '1px solid rgba(46, 204, 113, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {formatReportType(report.report_type)}
              </span>
            )}
          </div>
        </div>
        
        {error && (
          <div style={{ 
            background: 'rgba(231, 76, 60, 0.1)',
            border: '1px solid rgba(231, 76, 60, 0.3)',
            color: '#e74c3c',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{ 
            background: 'rgba(46, 204, 113, 0.1)',
            border: '1px solid rgba(46, 204, 113, 0.3)',
            color: '#27ae60',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {successMessage}
          </div>
        )}

        {/* 报告信息 */}
        {report && (
          <div className="report-detail-page__section" style={{
            background: 'rgba(250, 245, 236, 0.94)',
            borderRadius: '16px',
            padding: '30px',
            marginBottom: '20px',
            boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
            backdropFilter: 'blur(18px)'
          }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: '20px',
              color: '#2c3e50',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {t('reports.reportInfo')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reportId')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.id}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.status')}:</strong> 
                <span style={{ 
                  marginInlineStart: '8px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  ...(report.status === 'pending' ? {
                    background: 'rgba(241, 196, 15, 0.1)',
                    color: '#f39c12',
                    border: '1px solid rgba(241, 196, 15, 0.3)'
                  } : report.status === 'approved' ? {
                    background: 'rgba(46, 204, 113, 0.1)',
                    color: '#27ae60',
                    border: '1px solid rgba(46, 204, 113, 0.3)'
                  } : {
                    background: 'rgba(231, 76, 60, 0.1)',
                    color: '#e74c3c',
                    border: '1px solid rgba(231, 76, 60, 0.3)'
                  })
                }}>
                  {t(`reports.${report.status}`)}
                </span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.clinicId')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.clinic_id || t('reports.notSpecified')}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.clinicName')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.clinic_name || t('reports.notSpecified')}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.visitId')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.visit_id}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.visitType')}:</strong>
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatVisitType(report.visit_type)}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.createdAt')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatDate(report.created_at)}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.relatedVisits')}:</strong>
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatRelatedVisits(report.related_visit_ids)}</span>
              </p>
              {report.related_visit_ids?.length > 0 && (
                <div className="visit-chip-list" style={{ paddingInlineStart: '15px', marginTop: '2px' }}>
                  {report.related_visit_ids.map((visitId) => (
                    <Link key={`report-view-related-${visitId}`} to={buildRelatedVisitLink(visitId)} className="visit-chip">
                      #{visitId}
                    </Link>
                  ))}
                </div>
              )}
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reviewer')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.reviewer_name || t('reports.notReviewed')}</span>
              </p>
              <p style={{ margin: '8px 0', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reviewedAt')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{formatDate(report.reviewed_at)}</span>
              </p>
            </div>
            {report.review_note && (
              <div style={{ marginTop: '20px', textAlign: 'start', paddingInlineStart: '15px' }}>
                <strong style={{ color: '#2c3e50' }}>{t('reports.reviewNote')}:</strong> 
                <span style={{ marginInlineStart: '8px', color: '#34495e' }}>{report.review_note}</span>
              </div>
            )}

            {showExaminationOrdersSection && (
              <ReportExaminationOrders
                orders={examinationOrders}
                title={t('reports.examinationOrdersSectionTitle')}
                description={t('reports.examinationOrdersReadOnlyHint')}
                emptyMessage={t('reports.noExaminationOrders')}
                t={t}
              />
            )}

            {showPrescriptionSection && (
              <ReportPrescriptions
                items={prescriptionItems}
                title={t('reports.prescriptionSectionTitle')}
                description={t('reports.prescriptionReadOnlyDescription')}
                emptyMessage={t('reports.noPrescriptionItems')}
                t={t}
              />
            )}

            {showAppointmentOrdersSection && (
              <div ref={appointmentOrdersSectionRef} style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <div style={{ display: 'grid', gap: '0.55rem', marginBottom: '16px' }}>
                  <h4 className="report-subsection-title">
                    {t('reports.appointmentOrdersSectionTitle')}
                  </h4>
                  <span style={{ color: '#556476', fontSize: '14px', lineHeight: 1.6 }}>
                    {canCreateAppointmentOrders
                      ? t('reports.appointmentOrdersCreateHint')
                      : t('reports.appointmentOrdersReadOnlyHint')}
                  </span>
                </div>

                {appointmentOrders.length > 0 ? (
                  <div className="mini-card-grid">
                    {appointmentOrders.map((order) => (
                      <div
                        key={order.id}
                        className="mini-card"
                        style={{ background: 'rgba(255, 255, 255, 0.55)', borderColor: 'rgba(0, 0, 0, 0.08)' }}
                      >
                        <div className="mini-card__header">
                          <span className="mini-card__title" style={{ color: '#14253a' }}>
                            {getAppointmentOrderDisplay(order)}
                          </span>
                          <span style={{ color: '#667085', fontSize: '13px' }}>#{order.id}</span>
                        </div>
                        <div className="mini-card__meta" style={{ color: '#556476' }}>
                          {t('reports.sourceClinic')}: {formatClinicLabel(order.source_clinic_name, order.source_clinic_id)}
                        </div>
                        <div className="mini-card__meta" style={{ color: '#556476' }}>
                          {t('reports.claimClinic')}: {formatClinicLabel(order.claim_clinic_name, order.claim_clinic_id)}
                        </div>
                        <div className="inline-link-row" style={{ marginTop: '0.15rem' }}>
                          <Link className="inline-link" to={buildAppointmentQueueLink(order.id)}>
                            {t('reports.openAppointmentQueue')}
                          </Link>
                          <Link className="inline-link" to={buildRelatedVisitLink(order.source_visit_id)}>
                            {t('reports.openSourceVisit')}
                          </Link>
                          {order.source_report_id && (
                            <Link className="inline-link" to={`/reports/${order.source_report_id}/view`}>
                              {t('reports.openSourceReport')}
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '16px 18px',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.46)',
                    color: '#556476',
                    border: '1px dashed rgba(0, 0, 0, 0.12)'
                  }}>
                    {t('reports.noAppointmentOrders')}
                  </div>
                )}

                {canCreateAppointmentOrders && (
                  <div style={{
                    marginTop: '18px',
                    padding: '20px',
                    borderRadius: '14px',
                    background: 'rgba(255, 255, 255, 0.56)',
                    border: '1px solid rgba(0, 0, 0, 0.08)'
                  }}>
                    <div style={{ display: 'grid', gap: '0.45rem', marginBottom: '16px' }}>
                      <h5 className="report-subsection-title report-subsection-title--compact">
                        {t('reports.appointmentOrdersCreateTitle')}
                      </h5>
                      <span style={{ color: '#667085', fontSize: '13px', lineHeight: 1.6 }}>
                        {t('reports.appointmentOrdersCreateDescription')}
                      </span>
                    </div>

                    <div style={{ display: 'grid', gap: '12px' }}>
                      {appointmentDrafts.map((draft, index) => (
                        <div
                          key={`appointment-draft-view-${index}`}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(10rem, 13rem) minmax(0, 1fr) auto',
                            gap: '12px',
                            alignItems: 'start'
                          }}
                        >
                          <select
                            value={draft.check_type}
                            onChange={(event) => updateAppointmentDraft(index, 'check_type', event.target.value)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '2px solid rgba(102, 126, 234, 0.2)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: 'rgba(255, 248, 241, 0.88)',
                              color: '#1a202c',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">{t('reports.selectCheckType')}</option>
                            {APPOINTMENT_ORDER_OPTION_GROUPS.map((group) => (
                              <optgroup key={group.id} label={getAppointmentOrderGroupLabel(group, t)}>
                                {group.options.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {t(option.labelKey)}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>

                          <input
                            type="text"
                            value={draft.description}
                            onChange={(event) => updateAppointmentDraft(index, 'description', event.target.value)}
                            placeholder={t('reports.appointmentOrdersDescriptionPlaceholder')}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '2px solid rgba(102, 126, 234, 0.2)',
                              borderRadius: '8px',
                              fontSize: '14px',
                              background: 'rgba(255, 248, 241, 0.88)',
                              color: '#1a202c',
                              boxSizing: 'border-box',
                              outline: 'none',
                            }}
                          />

                          <button
                            type="button"
                            onClick={() => removeAppointmentDraft(index)}
                            disabled={creatingAppointmentOrders}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '8px',
                              border: '1px solid rgba(102, 126, 234, 0.3)',
                              background: 'transparent',
                              color: '#4a5568',
                              cursor: creatingAppointmentOrders ? 'not-allowed' : 'pointer',
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              opacity: creatingAppointmentOrders ? 0.5 : 1,
                            }}
                          >
                            {t('reports.remove')}
                          </button>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '16px' }}>
                      <button
                        type="button"
                        onClick={addAppointmentDraft}
                        disabled={creatingAppointmentOrders}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '999px',
                          border: '1px solid rgba(67, 214, 197, 0.4)',
                          background: 'linear-gradient(135deg, rgba(67, 214, 197, 0.2), rgba(67, 214, 197, 0.08))',
                          color: '#1a4a42',
                          fontWeight: '600',
                          cursor: creatingAppointmentOrders ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          opacity: creatingAppointmentOrders ? 0.5 : 1,
                        }}
                      >
                        {t('reports.addAppointmentOrder')}
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateAppointmentOrders}
                        disabled={creatingAppointmentOrders}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '999px',
                          border: 'none',
                          background: 'linear-gradient(135deg, #d4a96a, #c8935a)',
                          color: '#0b1727',
                          fontWeight: '600',
                          cursor: creatingAppointmentOrders ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          opacity: creatingAppointmentOrders ? 0.5 : 1,
                        }}
                      >
                        {creatingAppointmentOrders ? t('reports.appointmentOrdersCreating') : t('reports.appointmentOrdersSubmit')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* 报告内容 */}
        <div className="report-detail-page__section" style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <div className="report-section-title-row report-section-title-row--document" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ 
              marginTop: 0, 
              marginBottom: 0,
              color: '#2c3e50',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              {t('reports.reportContent')}
            </h3>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={handleExportMarkdown}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('secondary'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title={t('reports.exportMarkdown')}
              >
                📥 MD
              </button>

              <button
                type="button"
                onClick={handleExportPDF}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('primary'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title={t('reports.exportPdf')}
              >
                📄 PDF
              </button>

              <button
                type="button"
                onClick={handleExportDOCX}
                disabled={!reportContent || reportContent.trim() === ''}
                style={{
                  ...getButtonStyle('success'),
                  opacity: (!reportContent || reportContent.trim() === '') ? 0.4 : 1,
                  cursor: (!reportContent || reportContent.trim() === '') ? 'not-allowed' : 'pointer',
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
                onMouseEnter={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, true)}
                onMouseLeave={(e) => (reportContent && reportContent.trim() !== '') && handleButtonHover(e, false)}
                title={t('reports.exportWord')}
              >
                📝 Word
              </button>
            </div>
          </div>
          
          {reportContent ? (
            <div
              className="markdown-content markdown-content--paper"
              lang={reportLocale}
              dir={reportDirection}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ node, children, ...props }) => <h2 {...props}>{children}</h2>
                }}
              >
                {reportContent}
              </ReactMarkdown>
            </div>
          ) : (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              {t('reports.noContent')}
            </p>
          )}
        </div>

        <HealthHouseCheckList visitId={report?.visit_id} />

        {/* 旧检查项列表，仅保留代码以兼容历史流程。 */}
        {SHOW_LEGACY_CHECK_SECTIONS && (
        <div className="report-detail-page__section" style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '20px',
            color: '#2c3e50',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {t('reports.checksList')}
          </h3>
          
          {checksLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#667eea',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }}></div>
              <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingChecks')}</p>
            </div>
          ) : checksError ? (
            <div style={{ 
              color: '#ff416c', 
              marginBottom: '20px', 
              textAlign: 'center',
              background: 'rgba(255, 65, 108, 0.1)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 65, 108, 0.3)',
              fontWeight: '500'
            }}>
              {checksError}
            </div>
          ) : checks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '1.1rem', padding: '50px' }}>
              {t('reports.noChecksData')}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                  }}>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '30%', color: '#14253a' }}>
                      {t('reports.checkType')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '25%', color: '#14253a' }}>
                      {t('reports.createdAt')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '45%', color: '#14253a' }}>
                      {t('reports.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((check, index) => (
                    <React.Fragment key={check.id || index}>
                      <tr style={{ 
                        transition: 'background-color 0.3s ease',
                        '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.05)' }
                      }}>
                        <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                          <span style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)',
                            color: '#2980b9',
                            border: '1px solid rgba(52, 152, 219, 0.3)'
                          }}>
                            {getCheckTypeDisplay(check.check_type)}
                          </span>
                        </td>
                        <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                          {formatDate(check.created_at)}
                        </td>
                        <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                          <button 
                            onClick={() => toggleCheckDetails(index)}
                            style={{
                              ...getButtonStyle('secondary'),
                              padding: '8px 16px',
                              fontSize: '14px',
                              minWidth: 'auto'
                            }}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                          >
                            {t('reports.viewDetails')}
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan="3" style={{ padding: '0' }}>
                          <div 
                            id={`check-details-${index}`}
                            style={{ 
                              display: 'none',
                              padding: '20px',
                              background: 'rgba(102, 126, 234, 0.02)',
                              borderTop: '1px solid rgba(102, 126, 234, 0.1)'
                            }}
                          >
                            {renderCheckMetadata(check)}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* 待检查项目列表 */}
        {SHOW_LEGACY_CHECK_SECTIONS && (
        <div className="report-detail-page__section" style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '20px',
            color: '#e67e22',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {t('reports.pendingChecksList')}
          </h3>
          
          {pendingChecksLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <div style={{ 
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid rgba(0,0,0,.1)',
                borderRadius: '50%',
                borderTopColor: '#f39c12',
                animation: 'spin 1s linear infinite',
                marginBottom: '20px'
              }}></div>
              <p style={{ fontSize: '1.1rem', color: '#666' }}>{t('reports.loadingPendingChecks')}</p>
            </div>
          ) : pendingChecksError ? (
            <div style={{ 
              color: '#ff416c', 
              marginBottom: '20px', 
              textAlign: 'center',
              background: 'rgba(255, 65, 108, 0.1)',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid rgba(255, 65, 108, 0.3)',
              fontWeight: '500'
            }}>
              {pendingChecksError}
            </div>
          ) : pendingChecks.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', fontSize: '1.1rem', padding: '50px' }}>
              {t('reports.noPendingChecksData')}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="paper-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)'
                  }}>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '18%', color: '#14253a' }}>
                      {t('reports.checkType')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '12%', color: '#14253a' }}>
                      {t('reports.status')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '15%', color: '#14253a' }}>
                      {t('reports.createdAt')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '22%', color: '#14253a' }}>
                      {t('reports.description')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '15%', color: '#14253a' }}>
                      {t('reports.reviewInfo')}
                    </th>
                    <th style={{ padding: '15px', textAlign: 'start', border: '1px solid rgba(0, 0, 0, 0.1)', fontWeight: '600', width: '18%', color: '#14253a' }}>
                      {t('reports.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pendingChecks.map((pendingCheck, index) => (
                    <tr key={pendingCheck.id || index} style={{ 
                      transition: 'background-color 0.3s ease',
                      '&:hover': { backgroundColor: 'rgba(243, 156, 18, 0.05)' }
                    }}>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        <span style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
                          color: '#e67e22',
                          border: '1px solid rgba(243, 156, 18, 0.3)'
                        }}>
                          {getCheckTypeDisplay(pendingCheck.check_type)}
                        </span>
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          ...(pendingCheck.status === 'pending' ? {
                            background: 'rgba(241, 196, 15, 0.1)',
                            color: '#f39c12',
                            border: '1px solid rgba(241, 196, 15, 0.3)'
                          } : pendingCheck.status === 'approved' ? {
                            background: 'rgba(46, 204, 113, 0.1)',
                            color: '#27ae60',
                            border: '1px solid rgba(46, 204, 113, 0.3)'
                          } : pendingCheck.status === 'completed' ? {
                            background: 'rgba(52, 152, 219, 0.1)',
                            color: '#3498db',
                            border: '1px solid rgba(52, 152, 219, 0.3)'
                          } : {
                            background: 'rgba(231, 76, 60, 0.1)',
                            color: '#e74c3c',
                            border: '1px solid rgba(231, 76, 60, 0.3)'
                          })
                        }}>
                          {t(`reports.pendingCheckStatus.${pendingCheck.status}`)}
                        </span>
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {formatDate(pendingCheck.created_at)}
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {pendingCheck.description || t('reports.noDescription')}
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {pendingCheck.status === 'pending' ? (
                          <div style={{ 
                            color: '#f39c12', 
                            fontSize: '14px',
                            fontStyle: 'italic',
                            background: 'rgba(241, 196, 15, 0.1)',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '1px solid rgba(241, 196, 15, 0.3)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {t('reports.pendingReviewNote')}
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: '#556476', fontSize: '14px' }}>
                            {pendingCheck.reviewer_name && (
                              <div>{t('reports.reviewedBy')}: {pendingCheck.reviewer_name}</div>
                            )}
                            {pendingCheck.reviewed_at && (
                              <div style={{ fontSize: '12px', marginTop: '4px', color: '#627487' }}>
                                {formatDate(pendingCheck.reviewed_at)}
                              </div>
                            )}
                            {pendingCheck.review_note && (
                              <div style={{ 
                                fontSize: '12px', 
                                marginTop: '4px', 
                                fontStyle: 'italic',
                                color: '#556476'
                              }}>
                                {t('reports.note')}: {pendingCheck.review_note}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '15px', border: '1px solid rgba(0, 0, 0, 0.1)', color: '#334255' }}>
                        {pendingCheck.status === 'approved' ? (
                          <button
                            onClick={() => openResultModal(pendingCheck)}
                            style={{
                              ...getButtonStyle('success'),
                              padding: '8px 16px',
                              fontSize: '14px',
                              minWidth: 'auto'
                            }}
                            onMouseEnter={(e) => handleButtonHover(e, true)}
                            onMouseLeave={(e) => handleButtonHover(e, false)}
                          >
                            {t('reports.inputResult')}
                          </button>
                        ) : (
                          <span style={{ 
                            color: '#999', 
                            fontSize: '14px', 
                            fontStyle: 'italic' 
                          }}>
                            {pendingCheck.status === 'completed' ? t('reports.pendingCheckStatus.completed') : t('reports.notAvailable')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* 对话消息列表 */}
        <div className="report-detail-page__section" style={{
          background: 'rgba(250, 245, 236, 0.94)',
          borderRadius: '16px',
          padding: '30px',
          marginTop: '20px',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.16)',
          backdropFilter: 'blur(18px)'
        }}>
          <ReportConversationHistory
            messages={messages}
            loading={messagesLoading}
            error={messagesError}
            show={showConversations}
            onToggle={() => setShowConversations(!showConversations)}
            visitType={visit?.visit_type || report?.visit_type}
            conversationLanguage={visit?.language || report?.language}
            toggleButtonStyle={{
              ...getButtonStyle('secondary'),
              padding: '8px 16px',
              fontSize: '14px',
              minWidth: 'auto'
            }}
            onToggleButtonMouseEnter={(e) => handleButtonHover(e, true)}
            onToggleButtonMouseLeave={(e) => handleButtonHover(e, false)}
          />
        </div>
      </div>

      {/* 检查结果输入模态框 */}
      {showResultModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="report-detail-page__modal" style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: 0,
                color: '#2c3e50',
                fontSize: '1.5rem',
                fontWeight: '600'
              }}>
                {t('reports.inputCheckResult')}
              </h3>
              <button
                onClick={closeResultModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                  padding: '5px'
                }}
              >
                ✕
              </button>
            </div>

            {selectedPendingCheck && (
              <div style={{
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: 'rgba(52, 152, 219, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(52, 152, 219, 0.3)'
              }}>
                <div style={{ fontWeight: '600', color: '#2980b9', marginBottom: '5px' }}>
                  {selectedPendingCheck.description}
                </div>
                <div style={{ fontSize: '14px', color: '#666' }}>
                  {t('reports.checkType')}: {getCheckTypeDisplay(selectedPendingCheck.check_type)}
                </div>
              </div>
            )}

            {submitError && (
              <div style={{
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                border: '1px solid rgba(231, 76, 60, 0.3)',
                color: '#e74c3c',
                padding: '10px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px'
              }}>
                {submitError}
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                {t('reports.inputType')}
              </label>
              <select
                value={resultInputType}
                onChange={(e) => setResultInputType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
              >
                <option value="text">{t('reports.freeText')}</option>
                <option value="select">{t('reports.selectResult')}</option>
              </select>
            </div>

            {resultInputType === 'text' ? (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2c3e50'
                }}>
                  {t('reports.checkResult')}
                </label>
                <textarea
                  value={resultText}
                  onChange={(e) => setResultText(e.target.value)}
                  placeholder={t('reports.enterCheckResult')}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    color: '#1a202c'
                  }}
                />
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#2c3e50'
                }}>
                  {t('reports.checkResult')}
                </label>
                <select
                  value={resultValue}
                  onChange={(e) => setResultValue(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                >
                  <option value="阴性">{t('reports.negative')}</option>
                  <option value="阳性">{t('reports.positive')}</option>
                  <option value="正常">{t('reports.normal')}</option>
                  <option value="异常">{t('reports.abnormal')}</option>
                </select>
              </div>
            )}

            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                {t('reports.additionalDescription')} ({t('reports.optional')})
              </label>
              <textarea
                value={resultDescription}
                onChange={(e) => setResultDescription(e.target.value)}
                placeholder={t('reports.enterAdditionalInfo')}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '16px',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  color: '#1a202c'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeResultModal}
                disabled={submittingResult}
                style={{
                  ...getButtonStyle('dark'),
                  padding: '12px 24px'
                }}
                onMouseEnter={(e) => !submittingResult && handleButtonHover(e, true)}
                onMouseLeave={(e) => !submittingResult && handleButtonHover(e, false)}
              >
                {t('common.cancel')}
              </button>
              
              <button
                onClick={submitResult}
                disabled={submittingResult || (!resultText.trim() && resultInputType === 'text') || (!resultValue && resultInputType === 'select')}
                style={{
                  ...getButtonStyle('success'),
                  padding: '12px 24px',
                  opacity: submittingResult || (!resultText.trim() && resultInputType === 'text') || (!resultValue && resultInputType === 'select') ? 0.6 : 1
                }}
                onMouseEnter={(e) => !submittingResult && handleButtonHover(e, true)}
                onMouseLeave={(e) => !submittingResult && handleButtonHover(e, false)}
              >
                {submittingResult ? t('reports.submitting') : t('reports.submitResult')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportView;
